// /var/www/ohcampus_frontend_source/src/components/SimulatorLeadsPanel.jsx
// ─────────────────────────────────────────────────────────────────────────
//  Lightweight "Choice Entry Simulator Lead Capture" panel:
//   - Add a prospect with just Name + Phone (+ optional alt phone / notes)
//   - List your captured prospects
//   - One-click "Invite via WhatsApp" with a branded simulator template
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  UserPlus, MessageCircle, Phone, Loader2, Send, Trash2, Copy, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { simulatorLeadsAPI } from '../lib/api';

export default function SimulatorLeadsPanel() {
  const [form, setForm] = useState({ name: '', phone: '', alt_phone: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waLead, setWaLead] = useState(null);
  const [waPhoneChoice, setWaPhoneChoice] = useState('primary');
  const [waPreview, setWaPreview] = useState(null);
  const [waLoading, setWaLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await simulatorLeadsAPI.list({ scope: 'me', limit: 200 });
      setItems(r.data.items || []);
    } catch (_e) { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const n = (form.name || '').trim();
    const p = (form.phone || '').replace(/\D/g, '');
    if (n.length < 2 || p.length < 8) {
      toast.error('Name and a valid phone (8+ digits) are required');
      return;
    }
    setSaving(true);
    try {
      await simulatorLeadsAPI.create({
        name:      n,
        phone:     form.phone.trim(),
        alt_phone: form.alt_phone.trim() || null,
        notes:     form.notes.trim() || null,
      });
      toast.success('Prospect captured');
      setForm({ name: '', phone: '', alt_phone: '', notes: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const removeLead = async (id) => {
    if (!window.confirm('Remove this prospect from your simulator outreach list?')) return;
    try {
      await simulatorLeadsAPI.remove(id);
      toast.success('Removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove');
    }
  };

  // Open the WhatsApp dialog for a given lead
  const openWA = (lead) => {
    setWaLead(lead);
    setWaPhoneChoice('primary');
    setWaPreview(null);
  };

  // Build the preview whenever WA dialog opens or phone choice flips
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!waLead) return;
      setWaPreview(null);
      setWaLoading(true);
      try {
        const r = await simulatorLeadsAPI.whatsapp(waLead.id, { phone_choice: waPhoneChoice });
        if (!cancelled) setWaPreview(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Could not build invite');
      } finally {
        if (!cancelled) setWaLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [waLead, waPhoneChoice]);

  const doSend = () => {
    if (!waPreview?.whatsapp_url) return;
    window.open(waPreview.whatsapp_url, '_blank', 'noopener,noreferrer');
    toast.success('Opening WhatsApp…');
    // refresh the list so status reflects "Invited"
    load();
  };
  const doCopy = async () => {
    if (!waPreview?.message) return;
    try { await navigator.clipboard.writeText(waPreview.message); toast.success('Copied'); }
    catch (_e) { toast.error('Could not copy'); }
  };

  return (
    <Card className="border-0 shadow-sm bg-gradient-to-br from-white via-emerald-50/40 to-fuchsia-50/30" data-testid="sim-leads-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <UserPlus className="h-5 w-5 text-emerald-600" />
          Choice Entry Simulator — Lead Capture
          <Badge variant="outline" className="ml-2 bg-emerald-50 border-emerald-200 text-emerald-700">
            {items.length} prospect{items.length === 1 ? '' : 's'}
          </Badge>
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Quick-capture a prospect with just <strong>name</strong> + <strong>phone</strong> and send them the simulator invite over WhatsApp in one click.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Capture form */}
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="sim-leads-form">
          <div>
            <Label className="text-xs">Candidate Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Tanvi Rao" data-testid="sim-leads-name" />
          </div>
          <div>
            <Label className="text-xs">Contact Number *</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile" data-testid="sim-leads-phone" />
          </div>
          <div>
            <Label className="text-xs">Alternate Number</Label>
            <Input value={form.alt_phone} onChange={(e) => setForm({ ...form, alt_phone: e.target.value })} placeholder="optional" data-testid="sim-leads-alt-phone" />
          </div>
          <div className="flex items-end gap-2">
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" data-testid="sim-leads-notes" />
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0" data-testid="sim-leads-add">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}
              Add
            </Button>
          </div>
        </form>

        {/* List */}
        <div className="overflow-x-auto border border-slate-100 rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Captured</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">
                  No prospects yet. Capture one above to send your first simulator invite.
                </TableCell></TableRow>
              ) : items.map((l) => (
                <TableRow key={l.id} className="hover:bg-slate-50" data-testid={`sim-lead-row-${l.id}`}>
                  <TableCell>
                    <div className="font-medium text-slate-900 text-sm">{l.name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-mono text-xs text-slate-700">{l.phone}</div>
                    {l.alt_phone && <div className="font-mono text-[11px] text-slate-400">{l.alt_phone}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-xs truncate">{l.notes || '—'}</TableCell>
                  <TableCell className="text-xs text-slate-500">{(l.created_at || '').slice(0, 10)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${l.last_invited_at ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                      {l.last_invited_at ? 'Invited' : 'New'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openWA(l)} data-testid={`sim-lead-wa-${l.id}`}>
                        <MessageCircle className="h-3 w-3 mr-1" /> WhatsApp
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeLead(l.id)} data-testid={`sim-lead-delete-${l.id}`}>
                        <Trash2 className="h-3 w-3 text-rose-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* WhatsApp Dialog */}
      <Dialog open={!!waLead} onOpenChange={(o) => { if (!o) { setWaLead(null); setWaPreview(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sim-lead-wa-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-600" />
              Invite to Choice Entry Simulator
              {waLead && <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-700">{waLead.name}</Badge>}
            </DialogTitle>
            <DialogDescription>
              A branded WhatsApp invite will open in a new tab with the message pre-filled.
            </DialogDescription>
          </DialogHeader>

          <div>
            <Label className="text-xs">Send to</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              <button type="button" onClick={() => setWaPhoneChoice('primary')}
                className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition ${
                  waPhoneChoice === 'primary'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'
                }`}
                data-testid="sim-lead-wa-primary">
                <Phone className="h-3 w-3" /> Primary: {waLead?.phone}
              </button>
              {waLead?.alt_phone && (
                <button type="button" onClick={() => setWaPhoneChoice('alt')}
                  className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition ${
                    waPhoneChoice === 'alt'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400'
                  }`}
                  data-testid="sim-lead-wa-alt">
                  <Phone className="h-3 w-3" /> Alt: {waLead.alt_phone}
                </button>
              )}
            </div>
          </div>

          <div className="border border-slate-100 rounded-lg overflow-hidden">
            <div className="bg-emerald-50 border-b border-emerald-100 px-3 py-2 text-xs font-semibold text-emerald-800">
              WhatsApp Preview
            </div>
            <div
              data-testid="sim-lead-wa-preview"
              className="text-xs p-3 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%3E%3Crect%20width%3D%2220%22%20height%3D%2220%22%20fill%3D%22%23ECE5DD%22%2F%3E%3C%2Fsvg%3E')] min-h-[180px]"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {waLoading ? (
                <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Building invite…</div>
              ) : waPreview?.message ? (
                <div className="bg-white rounded-lg shadow-sm px-3 py-2 leading-relaxed text-slate-800">
                  {waPreview.message.split('\n').map((line, idx) => {
                    let html = line
                      .replace(/\*(.+?)\*/g, '<strong>$1</strong>')
                      .replace(/_(.+?)_/g, '<em class="text-slate-500">$1</em>');
                    return <div key={idx} dangerouslySetInnerHTML={{ __html: html || '&nbsp;' }} />;
                  })}
                </div>
              ) : (
                <span className="text-slate-400 italic">Loading…</span>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={doCopy} disabled={!waPreview?.message} data-testid="sim-lead-wa-copy">
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button onClick={doSend} disabled={!waPreview?.whatsapp_url}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="sim-lead-wa-send">
              <Send className="h-4 w-4 mr-1" /> Open in WhatsApp <ExternalLink className="h-3 w-3 ml-1 opacity-70" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
